import React, { FC, useEffect } from 'react'
import { Box, Heading, Link, Text } from '@chakra-ui/react'
import { useLocalization, Trans } from '@rw1/localization'
import { useColor } from '@rw1/shared-lib'
import { CUSTOM_PROTOCOL_SCHEMA } from '../../config'
import { Layout } from './Layout'


const APP_LOGIN_ENDPOINT = 'auth'
const APP_LOGOUT_ENDPOINT = 'logout'

export const LoginSuccess: FC = () => {
  const { localize } = useLocalization()
  const redText = useColor({ palette: 'colors', nuance: 'red' })

  // Read Auth0 params from the URL
  const urlParams = new URLSearchParams(window.location.search)
  const code = urlParams.get('code')
  const state = urlParams.get('state')

  const error = urlParams.get('error')
  const error_description = urlParams.get('error_description')

  // Compute scheme at runtime from host, then sanitize.
  const scheme = CUSTOM_PROTOCOL_SCHEMA

  // Build the deep link the desktop app expects (host=auth with query).
  const appLoginUrl =
    `${scheme}://${APP_LOGIN_ENDPOINT}` +
    `?code=${encodeURIComponent(code ?? '')}` +
    `&state=${encodeURIComponent(state ?? '')}`

  const documentTitle = (error ?? error_description) ?
    "Log in error" : "Log in success"

  useEffect(() => {
    // eslint-disable-next-line functional/immutable-data
    document.title = `${documentTitle} | realworld one`

    // Open the desktop app deep link; use location.replace for fewer history entries.
    const t1 = setTimeout(() => { window.location.replace(appLoginUrl) }, 400)

    // If Auth0 returned an error, ask the desktop app to clean up.
    const t2 = setTimeout(() => {
      if (error_description) {
        window.location.replace(`${scheme}://${APP_LOGOUT_ENDPOINT}`)
      }
    }, 1800)

    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [appLoginUrl, documentTitle, error_description, scheme])

  // Error screen (shows details and avoids trying to deep link blindly)
  if (error ?? error_description) {
    return (
      <Layout>
        <Heading fontSize={28}>
          {error ? localize('login.error.title') : localize('login.success.title')}
        </Heading>
        <Text>{localize('login.success.error')}</Text>
        <Box color={redText} css={{ marginTop: '24px' }} fontSize={12}>
          <Text fontSize="16" fontWeight="bold">{error}</Text>
          <Text>{error_description}</Text>
        </Box>
      </Layout>
    )
  }

  // If we didn’t get code/state, show a generic error (no deep link).
  if (!code || !state) {
    return (
      <Layout>
        <Text>{localize('login.success.error')}</Text>
      </Layout>
    )
  }

  // Success UI with button + hidden fallback link
  return (
    <Layout>
      <Heading fontSize={28}>{localize('login.success.title')}</Heading>
      <Text>{localize('login.success.text')}</Text>

      <Box css={{ marginTop: 'auto' }} fontSize={12}>
        <Trans
          components={{
            button: <Link fontWeight="bold" href={appLoginUrl} />,
          }}
          i18nKey="login.success.relaunch_app"
        />
      </Box>

      {/* Hidden link fallback */}
      <Link aria-hidden="true" display="none" href={appLoginUrl} />
    </Layout>
  )
}
